import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Home, Send, CheckCircle, ImageIcon, Building, Rocket, PhoneCall, BadgeCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from "../lib/api";
import { ImagesField } from './ui/images-field';
import { businessInfo } from '../mock';


const ListYourProperty = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.title = 'List Your Property FREE | CHIV Properties Greater Noida';
  }, []);

  // Prefill listingType from URL (?listingType=rent or ?listingType=sale)
  const initialListingType = searchParams.get('listingType') === 'rent' ? 'rent' : 'sale';

  const [formData, setFormData] = useState({
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    propertyName: '',
    location: 'Sector Chi V, Greater Noida',
    propertyType: 'Apartment',
    listingType: initialListingType,
    bhk: '',
    price: '',
    area: '',
    description: '',
    images: [],
    amenities: '',
    highlights: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.ownerName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    if (!formData.ownerPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const submissionData = {
        ...formData,
        images: formData.images,
        amenities: formData.amenities.split('\n').filter(a => a.trim()),
        highlights: formData.highlights.split('\n').filter(h => h.trim())
      };
      
      await api.post(`/property-submissions`, submissionData);
      
      toast.success('Property submitted successfully! Our team will review and contact you soon.');
      setSubmitted(true);
      
    } catch (error) {
      console.error('Error submitting property:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit property. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center" data-testid="submission-success">
        <Card className="max-w-lg mx-4">
          <CardContent className="p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Property Submitted Successfully!</h2>
            <p className="text-gray-600 mb-6">Our team will review your property within 24 hours. We'll contact you at <span className="font-semibold">{formData.ownerPhone}</span> once approved.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700" data-testid="go-home-btn">
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              <Button onClick={() => { setSubmitted(false); setFormData({ ownerName: '', ownerPhone: '', ownerEmail: '', propertyName: '', location: 'Sector Chi V, Greater Noida', propertyType: 'Apartment', listingType: 'sale', bhk: '', price: '', area: '', description: '', images: [], amenities: '', highlights: '' }); }} variant="outline" data-testid="submit-another-btn">
                Submit Another Property
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Home className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">{businessInfo.name}</h1>
                <p className="text-xs text-gray-600 italic">{businessInfo.tagline}</p>
              </div>
            </div>
            <Button onClick={() => navigate('/')} variant="outline" data-testid="back-to-home-btn">
              Back to Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
              <Building className="h-5 w-5" />
              <span className="font-semibold">Free Property Listing</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              List Your Property for <span className="text-blue-600">FREE</span>
            </h1>
            <p className="text-lg text-gray-600">
              Reach thousands of potential buyers in Greater Noida. No hidden charges!
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            {[
              { icon: Rocket, title: 'Quick Listing', desc: 'Get listed within 24 hours' },
              { icon: PhoneCall, title: 'Direct Calls', desc: 'Buyers contact you directly' },
              { icon: BadgeCheck, title: '100% Free', desc: 'No commission, no charges' }
            ].map((benefit, i) => (
              <Card key={i} className="text-center" data-testid={`benefit-card-${i}`}>
                <CardContent className="p-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                    <benefit.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Listing Form */}
          <Card>
            <CardHeader>
              <CardTitle>Property Listing Form</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="list-property-form">
                {/* Owner Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Your Contact Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Your Name *</label>
                      <Input name="ownerName" value={formData.ownerName} onChange={handleInputChange} placeholder="Full name" required data-testid="owner-name-input" />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Phone Number *</label>
                      <Input name="ownerPhone" value={formData.ownerPhone} onChange={handleInputChange} placeholder="9876543210" required data-testid="owner-phone-input" />
                    </div>
                    {/* <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">Email (Optional)</label>
                      <Input name="ownerEmail" type="email" value={formData.ownerEmail} onChange={handleInputChange} placeholder="your@email.com" data-testid="owner-email-input" />
                    </div> */}
                  </div>
                </div>

                {/* Property Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Property Details</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">Property Title *</label>
                      <Input name="propertyName" value={formData.propertyName} onChange={handleInputChange} placeholder="e.g., Spacious 3BHK Apartment in Poorvanchal Royal City" required data-testid="property-name-input" />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Location *</label>
                      <Input name="location" value={formData.location} onChange={handleInputChange} placeholder="Sector, Society Name" required data-testid="property-location-input" />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">I want to *</label>
                      <div className="flex gap-3">
                        {[
                          { value: 'sale', label: 'Sell this property' },
                          { value: 'rent', label: 'Rent it out' },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, listingType: opt.value })}
                            className={`flex-1 px-4 py-3 rounded-md border text-sm font-medium transition-colors ${
                              formData.listingType === opt.value
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                            data-testid={`listing-type-${opt.value}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Property Type *</label>
                      <Select name="propertyType" value={formData.propertyType} onValueChange={(value) => setFormData({...formData, propertyType: value})}>
                        <SelectTrigger data-testid="property-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Apartment">Apartment</SelectItem>
                          <SelectItem value="Villa">Villa</SelectItem>
                          <SelectItem value="Penthouse">Penthouse</SelectItem>
                          <SelectItem value="Commercial">Commercial</SelectItem>
                          <SelectItem value="Plot">Plot/Land</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Configuration (BHK)</label>
                      <Input name="bhk" value={formData.bhk} onChange={handleInputChange} placeholder="e.g., 3BHK, 2BHK" data-testid="property-bhk-input" />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        {formData.listingType === 'rent' ? 'Expected Monthly Rent *' : 'Expected Selling Price *'}
                      </label>
                      <Input 
                        name="price" 
                        value={formData.price} 
                        onChange={handleInputChange} 
                        placeholder={formData.listingType === 'rent' ? 'e.g., 15,000 / month' : 'e.g., 65 Lac, 1.2 Cr'} 
                        required 
                        data-testid="property-price-input" 
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Area *</label>
                      <Input name="area" value={formData.area} onChange={handleInputChange} placeholder="e.g., 1450 sq.ft" required data-testid="property-area-input" />
                    </div>
                  </div>

                  {/* <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Property Description *</label>
                    <Textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Describe your property in detail..." rows={4} required data-testid="property-description-input" />
                  </div> */}
                </div>

                {/* Additional Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Additional Information (Optional)</h3>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-blue-600" />
                      Property Images
                    </label>
                    <ImagesField
                      value={formData.images}
                      onChange={(urls) => setFormData((f) => ({ ...f, images: urls }))}
                      kind="submission"
                      max={20}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Amenities</label>
                    <Textarea name="amenities" value={formData.amenities} onChange={handleInputChange} placeholder="24/7 Security&#10;Power Backup&#10;Lift&#10;Parking" rows={3} data-testid="property-amenities-input" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Highlights</label>
                    <Textarea name="highlights" value={formData.highlights} onChange={handleInputChange} placeholder="Vastu Compliant&#10;Gated Community&#10;Near Metro" rows={3} data-testid="property-highlights-input" />
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-green-800">
                    <p className="font-semibold mb-1">What happens after submission?</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Our team will review your property within 24 hours</li>
                      <li>Once approved, it will appear on our website</li>
                      <li>Interested buyers will contact you directly at {formData.ownerPhone || 'your number'}</li>
                      <li>100% FREE service - No charges at all!</li>
                    </ul>
                  </div>
                </div>

                {/* Submit Button */}
                <Button type="submit" size="lg" className="w-full bg-blue-600 hover:bg-blue-700" disabled={isSubmitting} data-testid="submit-property-btn">
                  <Send className="h-5 w-5 mr-2" />
                  {isSubmitting ? 'Submitting...' : 'Submit Property for FREE Listing'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p>Need help? Call us at <a href={`tel:${businessInfo.phone}`} className="text-blue-600 font-semibold">{businessInfo.phone}</a></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListYourProperty;
